import { useEffect } from 'react';
import { Modal, Form, Input, Select, Switch, Row, Col, message } from 'antd';
import demoModulesApi, { DemoModule } from '../../lib/demoModulesApi';

interface ModuleConfigModalProps {
  visible: boolean;
  module: DemoModule | null;
  onClose: (reload?: boolean) => void;
}

const ModuleConfigModal = ({ visible, module, onClose }: ModuleConfigModalProps) => {
  const [form] = Form.useForm();
  const isEditing = !!module;

  useEffect(() => {
    if (visible && module) {
      form.setFieldsValue(module);
    } else if (visible) {
      form.resetFields();
    }
  }, [visible, module, form]);

  const handleSubmit = async (values: any) => {
    // Convert sortOrder to number
    const payload = {
      ...values,
      sortOrder: values.sortOrder ? parseInt(values.sortOrder) : 0,
    };
    
    try {
      if (isEditing) {
        await demoModulesApi.updateModule(module!.id, payload);
        message.success('Module updated successfully');
      } else {
        await demoModulesApi.createModule(payload);
        message.success('Module created successfully');
      }
      onClose(true);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Operation failed');
    }
  };

  return (
    <Modal
      title={isEditing ? 'Edit Module' : 'Create Module'}
      open={visible}
      onCancel={() => onClose()}
      onOk={() => form.submit()}
      width={700}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          isEnabled: true,
          isVisible: true,
          sortOrder: 0,
          category: 'work',
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Key"
              name="key"
              rules={[
                { required: true, message: 'Key is required' },
                { pattern: /^[a-z_]+$/, message: 'Only lowercase letters and underscores' },
              ]}
            >
              <Input placeholder="workorder" disabled={isEditing} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: 'Name is required' }]}
            >
              <Input placeholder="Work Orders" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Description" name="description">
          <Input.TextArea rows={2} placeholder="Module description" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Category"
              name="category"
              rules={[{ required: true }]}
            >
              <Select>
                <Select.Option value="work">Work Management</Select.Option>
                <Select.Option value="asset">Asset Management</Select.Option>
                <Select.Option value="inventory">Inventory</Select.Option>
                <Select.Option value="service">Service</Select.Option>
                <Select.Option value="admin">Administration</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Icon" name="icon">
              <Input placeholder="ToolOutlined" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Color" name="color">
              <Input placeholder="#ec4899" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Sort Order" name="sortOrder">
              <Input type="number" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Enabled" name="isEnabled" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Visible" name="isVisible" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ModuleConfigModal;
